import { DateFormats, formatDate } from '@strata/helper-functions';
import dayjs from 'dayjs';

import { EnrollmentSource } from 'services/memberHelpers';

export const ENROLLMENT_HISTORY_ROWS = [
    {
        key: 'enrollmentStatus',
        label: 'Enrollment Status',
        changedFields: ['enrollment_status'],
    },
    {
        key: 'coverageDates',
        label: 'Coverage Dates',
        changedFields: ['enrollment_start', 'enrollment_end'],
    },
    {
        key: 'enrollmentSource',
        label: 'Enrollment Source',
        changedFields: ['enrollment_source'],
    },
    {
        key: 'name',
        label: 'Name',
        changedFields: ['firstname', 'lastname'],
    },
    {
        key: 'gender',
        label: 'Gender',
        changedFields: ['gender'],
    },
    {
        key: 'dateOfBirth',
        label: 'Date of birth',
        changedFields: ['dateofbirth'],
    },
    {
        key: 'age',
        label: 'Age',
        changedFields: ['dateofbirth'],
    },
    {
        key: 'address',
        label: 'Address',
        changedFields: [
            'member_address1',
            'member_address2',
            'member_city',
            'member_state',
            'member_zip',
        ],
    },
    {
        key: 'phone',
        label: 'Phone',
        changedFields: ['phone', 'secondary_phone', 'member_phone'],
    },
    {
        key: 'email',
        label: 'Email',
        changedFields: ['email'],
    },
    {
        key: 'memberId',
        label: 'Member ID',
        changedFields: ['member_id', 'cardholder_id'],
    },
    {
        key: 'carrier',
        label: 'Carrier',
        changedFields: ['carrier'],
    },
    {
        key: 'account',
        label: 'Account',
        changedFields: ['account'],
    },
    {
        key: 'benefitGroup',
        label: 'Group ID',
        changedFields: ['benefit_group'],
    },
    {
        key: 'lineOfBusiness',
        label: 'Line of business',
        changedFields: ['line_of_business'],
    },
    {
        key: 'pharmacyCoverage',
        label: 'Pharmacy Coverage',
        changedFields: ['pharmacy_coverage'],
    },
    {
        key: 'insuranceType',
        label: 'Medical Coverage',
        changedFields: ['insurance_type'],
    },
    {
        key: 'relationshipCode',
        label: 'Relationship Code',
        changedFields: ['relationship_code'],
    },
    {
        key: 'isMarketplaceEligible',
        label: 'Marketplace',
        changedFields: ['is_marketplace_eligible'],
    },
    {
        key: 'isAso',
        label: 'ASO',
        changedFields: ['is_aso'],
    },
];

const RELATIONSHIP_CODE_TO_LABEL = {
    18: 'Self',
    1: 'Spouse',
    '01': 'Spouse',
    19: 'Child',
    20: 'Employee',
    10: 'Foster Child',
};

export const formatRelationshipCodeDisplay = (relationshipCode) => {
    if (relationshipCode == null) {
        return undefined;
    }

    return `${RELATIONSHIP_CODE_TO_LABEL[relationshipCode]} (${relationshipCode})`;
};

export const formatEnrollmentHistoryColumnTitle = (changedAt) => {
    const changedAtDate = dayjs(changedAt);
    const formattedChangedAt = formatDate(changedAt, DateFormats.DATE_TIME);
    const daysAgo = dayjs().startOf('day').diff(changedAtDate.startOf('day'), 'day');

    if (daysAgo <= 0) {
        return `${formattedChangedAt} (today)`;
    }

    return `${formattedChangedAt} (${daysAgo}d ago)`;
};

export const hasSnapshotRowChanged = (snapshot, rowKey) => {
    const rowConfig = ENROLLMENT_HISTORY_ROWS.find((row) => row.key === rowKey);

    if (!rowConfig) {
        return false;
    }

    return rowConfig.changedFields.some((fieldName) => snapshot.changedFields.includes(fieldName));
};

export const formatEnrollmentSourceDisplay = (enrollmentSource) =>
    EnrollmentSource[enrollmentSource] ?? enrollmentSource;

export const formatMarketplaceDisplay = (isMarketplaceEligible) => {
    if (isMarketplaceEligible == null) {
        return undefined;
    }

    return isMarketplaceEligible ? 'Yes' : 'No';
};

export const formatAsoDisplay = (isAso) => {
    if (isAso == null) {
        return undefined;
    }

    return isAso ? 'True' : 'False';
};

export const mapEnrollmentChange = (change) => ({
    sourceTable: change.source_table,
    fieldName: change.field_name,
    oldValue: change.old_value,
    newValue: change.new_value,
});

export const mapEnrollmentChangeHistory = (history) => ({
    fmhMemberId: history.fmh_member_id,
    enrollmentId: history.enrollment_id,
    memberId: history.member_id,
    firstName: history.firstname,
    lastName: history.lastname,
    dateOfBirth: history.dateofbirth,
    gender: history.gender,
    phone: history.phone,
    email: history.email,
    memberAddress1: history.member_address1,
    memberAddress2: history.member_address2,
    memberCity: history.member_city,
    memberState: history.member_state,
    memberZip: history.member_zip,
    enrollmentStart: history.enrollment_start,
    enrollmentEnd: history.enrollment_end,
    benefitGroup: history.benefit_group,
    benefitCode: history.benefit_code,
    insuranceType: history.insurance_type,
    relationshipCode: history.relationship_code,
    secondaryCoverageFlag: history.secondary_coverage_flag,
    carrier: history.carrier,
    account: history.account,
    memberPin: history.member_pin,
    lineOfBusiness: history.line_of_business,
    lobSegment: history.lob_segment,
    isAso: history.is_aso,
    isMarketplaceEligible: history.is_marketplace_eligible,
    pharmacyCoverage: history.pharmacy_coverage,
    alphaPrefix: history.alpha_prefix,
    secondaryPhone: history.secondary_phone,
    enterpriseMemberId: history.enterprise_member_id,
    enterprisePersonId: history.enterprise_person_id,
    payerId: history.payer_id,
    segmentId: history.segment_id,
    enrollmentStatus: history.enrollment_status,
    enrollmentSource: history.enrollment_source,
    pendingAt: history.pending_at,
    lastUpdated: history.last_updated,
    enrollmentChangeDate: history.enrollment_change_date,
    isFmhEnrollmentEnd: history.is_fmh_enrollment_end,
    changedAt: history.changed_at,
    changedFields: history.changed_fields ?? [],
    changes: (history.changes ?? []).map(mapEnrollmentChange),
});
